import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { cta, CTA_STYLES, CTA_COLORS } from "@/lib/cta";
import { BRAND } from "@/lib/brand";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { SiteHeader } from "@/components/layout/SiteHeader";
import { ScrollToTop } from "@/components/ui/scroll-to-top";

const THEME = BRAND;

const copy = {
  en: {
    hero: {
      eyebrow: "BUSINESS PARTNERS",
      title: "Join a trusted ecosystem",
      subtitle: "Connect with sellers and buyers ready to act and expand your reach.",
      primary: "Join the network",
      secondary: "Talk to an expert",
    },
    mission: {
      title: "Transactional Efficiency",
      description:
        "Optimize your practice by collaborating on structured, \"lender-ready,\" and centralized transactions. Join a select group of professionals dedicated to the successful transfer of businesses.",
    },
    journey: {
      steps: [
        { h: "Qualified Mandates", p: "Access deal flows where vendor due diligence has already been initiated, allowing you to focus on complex legal and tax structuring." },
        { h: "Collaborative Infrastructure", p: "Eliminate administrative friction with our unified dashboard. Centralize your communications with lenders, notaries, and CPAs for a seamless closing process." },
        { h: "Immigration & M&A Expertise", p: "Collaborate on high-value cases, particularly for international clients requiring synergy between business law and immigration law." },
      ],
    },
    benefits: {
      title: "Advantages",
      cards: [
        { h: "A flow of qualified clients", p: "Access concrete needs (due diligence, valuation, legal/CPA)." },
        { h: "Simplified collaboration", p: "Seamless integration into standardized transaction workflows." },
        { h: "A trusted network", p: "Increased visibility and credibility via a qualified ecosystem." },
      ],
    },
    faq: {
      title: "Frequently Asked Questions",
      items: [
        {
          q: "What types of professionals can join the network?",
          a: "Business lawyers, immigration lawyers, CPAs, notaries, and tax advisors are welcome to join our professional network."
        },
        {
          q: "How does the collaboration process work?",
          a: "Once registered, you'll receive qualified mandates matching your expertise. Our unified dashboard centralizes all communications and document sharing."
        },
        {
          q: "Is there a cost to join the network?",
          a: "Registration is free. We operate on a success-based model where you're compensated for completed transactions."
        },
        {
          q: "How are mandates assigned?",
          a: "Mandates are distributed based on your area of expertise, availability, and geographic location to ensure optimal client matching."
        },
      ],
    },
    cta: {
      title: "Ready to expand your practice?",
      subtitle: "Join our network of trusted professionals and access qualified mandates.",
      button: "Join now",
    },
  },
  fr: {
    hero: {
      eyebrow: "PARTENAIRES D'AFFAIRES",
      title: "Rejoignez un écosystème de confiance",
      subtitle: "Connectez‑vous avec des vendeurs et des acheteurs prêts à l'action et développez votre portée.",
      primary: "Je veux rejoindre le réseau",
      secondary: "Parler à un expert",
    },
    mission: {
      title: "L'Efficacité Transactionnelle",
      description:
        "Optimisez votre pratique en collaborant sur des transactions structurées, \"Lender-ready\" et centralisées. Rejoignez un cercle restreint de professionnels dédiés à la réussite du transfert d'entreprise.",
    },
    journey: {
      steps: [
        { h: "Mandats Qualifiés", p: "Accédez à des flux de dossiers où la préparation vendeur (Vendor Due Diligence) a déjà été amorcée, vous permettant de vous concentrer sur l'ingénierie juridique et fiscale complexe." },
        { h: "Infrastructure Collaborative", p: "Éliminez les frictions administratives grâce à notre tableau de bord unifié. Centralisez vos échanges avec les prêteurs, notaires et CPA pour un closing sans goulots d'étranglement." },
        { h: "Expertise Immigration & M&A", p: "Collaborez sur des dossiers à forte valeur ajoutée, notamment pour une clientèle internationale nécessitant une synergie entre droit des affaires et droit de l'immigration." },
      ],
    },
    benefits: {
      title: "Les avantages",
      cards: [
        { h: "Standardisation des flux", p: "Utilisez des protocoles de communication et de partage de documents standardisés (NDA, LOI, Data Room) pour accélérer vos dossiers." },
        { h: "Visibilité Institutionnelle", p: "Soyez le point de contact privilégié au moment critique du closing pour une base d'acheteurs et de vendeurs pré-qualifiés." },
        { h: "Synergie Interprofessionnelle", p: "Travaillez de concert avec des consultants opérationnels qui ont déjà préparé le terrain stratégique de l'entreprise." },
      ],
    },
    faq: {
      title: "Questions Fréquentes",
      items: [
        {
          q: "Quels types de professionnels peuvent rejoindre le réseau ?",
          a: "Les avocats d'affaires, avocats en immigration, CPA, notaires et conseillers fiscaux sont les bienvenus dans notre réseau professionnel."
        },
        {
          q: "Comment fonctionne le processus de collaboration ?",
          a: "Une fois inscrit, vous recevrez des mandats qualifiés correspondant à votre expertise. Notre tableau de bord unifié centralise toutes les communications et le partage de documents."
        },
        {
          q: "Y a-t-il un coût pour rejoindre le réseau ?",
          a: "L'inscription est gratuite. Nous fonctionnons sur un modèle basé sur le succès où vous êtes rémunéré pour les transactions complétées."
        },
        {
          q: "Comment les mandats sont-ils attribués ?",
          a: "Les mandats sont distribués en fonction de votre domaine d'expertise, de votre disponibilité et de votre localisation géographique pour assurer un appariement optimal avec les clients."
        },
      ],
    },
    cta: {
      title: "Prêt à développer votre pratique ?",
      subtitle: "Rejoignez notre réseau de professionnels de confiance et accédez à des mandats qualifiés.",
      button: "Rejoindre maintenant",
    },
  },
} as const;


export default function PartnersInternalLanding() {
  const [lang, setLang] = useState<'en' | 'fr'>('en');
  const t = useMemo(() => copy[lang], [lang]);
  const ctaTexts = cta('partner', lang);

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      {/* Header */}
      <SiteHeader lang={lang} onToggleLang={() => setLang(lang === 'en' ? 'fr' : 'en')} />

      {/* Hero */}
      <section className="w-full font-sans" style={{ background: `linear-gradient(180deg, ${THEME.navy}, ${THEME.navyDark})` }}>
        <div className="mx-auto max-w-7xl px-4 pt-10 pb-10">
          <p className="text-sm uppercase font-sans" style={{ color: THEME.silver }}>{t.hero.eyebrow}</p>
          <h1 className="mt-2 text-4xl md:text-5xl font-extrabold text-white font-sans">{t.hero.title}</h1>
          <p className="mt-3 max-w-3xl text-base md:text-lg text-white/85 font-sans">{t.hero.subtitle}</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/register/partner" className={`${CTA_STYLES.primary} font-sans`} style={{ background: `linear-gradient(90deg, ${CTA_COLORS.goldStart}, ${CTA_COLORS.goldEnd})` }}>{ctaTexts.primary}</Link>
            <Link to="/contact" aria-label={ctaTexts.aria} className={`${CTA_STYLES.secondary} border-white/30 text-white font-sans`}>{ctaTexts.secondary}</Link>
          </div>
        </div>
      </section>

      {/* Mission Section - Transactional Efficiency */}
      <section className="py-16 bg-gray-50">
        <div className="mx-auto max-w-4xl px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-sans" style={{ color: THEME.navy }}>
            {t.mission.title}
          </h2>
          <p className="mt-4 text-lg font-sans" style={{ color: THEME.graphite }}>
            {t.mission.description}
          </p>
        </div>
      </section>

      {/* Journey */}
      <section className="mx-auto max-w-7xl px-4 py-16">
        <div className="space-y-4">
          {t.journey.steps.map((s, i) => (
            <div key={i} className="rounded-xl border bg-white p-6 shadow-sm">
              <h3 className="font-semibold text-lg font-sans" style={{ color: THEME.navy }}>{s.h}</h3>
              <p className="mt-2 text-sm font-sans" style={{ color: THEME.graphite }}>{s.p}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits */}
      <section className="mx-auto max-w-7xl px-4 pb-16">
        <h2 className="text-2xl md:text-3xl font-semibold font-sans" style={{ color: THEME.navy }}>{t.benefits.title}</h2>
        <div className="mt-6 grid md:grid-cols-3 gap-6">
          {t.benefits.cards.map((c, i) => (
            <div key={i} className="rounded-2xl border bg-white p-6 shadow-sm">
              <h3 className="font-semibold font-sans" style={{ color: THEME.navy }}>{c.h}</h3>
              <p className="mt-1 text-sm font-sans" style={{ color: THEME.graphite }}>{c.p}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Learn More CTA */}
      <section className="py-12 bg-gray-50">
        <div className="mx-auto max-w-4xl px-4 text-center">
          <Link
            to="/general-landing"
            className="inline-block rounded-lg px-8 py-3 text-lg font-semibold font-sans"
            style={{ background: `linear-gradient(90deg, ${CTA_COLORS.goldStart}, ${CTA_COLORS.goldEnd})`, color: '#1f2937' }}
          >
           {lang === 'fr' ? 'En savoir plus' : 'Learn More'}
          </Link>
          <Link
            to="/partner/plans"
            className="inline-block rounded-lg px-8 py-3 text-lg font-semibold font-sans ml-4"
            style={{ background: '#CEAB65', color: '#1D314D', border: '2px solid #CEAB65' }}
          >
            {lang === 'fr' ? 'Voir les forfaits' : 'View Plans'}
          </Link>
        </div>
      </section>
      {/* FAQ Section */}

      {/* FAQ Section */}
      <section className="py-16 bg-[#F7F9FC]">
        <div className="mx-auto max-w-4xl px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center font-sans mb-10" style={{ color: THEME.navy }}>
            {t.faq.title}
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            {t.faq.items.map((item, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="bg-white rounded-xl border shadow-sm px-6"
              >
                <AccordionTrigger className="text-left font-semibold font-sans py-4" style={{ color: THEME.navy }}>
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm font-sans pb-4" style={{ color: THEME.graphite }}>
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16" style={{ background: `linear-gradient(180deg, ${THEME.navy}, ${THEME.navyDark})` }}>
        <div className="mx-auto max-w-4xl px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white font-sans">
            {t.cta.title}
          </h2>
          <p className="mt-4 text-lg text-white/85 font-sans">
            {t.cta.subtitle}
          </p>
          <div className="mt-8">
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                to="/register/partner"
                className="inline-block rounded-lg px-8 py-3 text-lg font-semibold font-sans"
                style={{ background: `linear-gradient(90deg, ${CTA_COLORS.goldStart}, ${CTA_COLORS.goldEnd})`, color: '#1f2937' }}
              >
                {t.cta.button}
              </Link>
              <Link
                to="/partner/plans"
                className="inline-block rounded-lg px-8 py-3 text-lg font-semibold font-sans"
                style={{ background: 'transparent', color: '#CEAB65', border: '2px solid #CEAB65' }}
              >
                {lang === 'fr' ? 'Voir les forfaits' : 'View Plans'}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <SiteFooter lang={lang} />
      <ScrollToTop />
    </div>
  );
}
